import { equal } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { BlockEmailsBanner } from '@/components/block-emails';
import { AddUsersBanner } from '@/components/users/AddUsersBanner';
import { UserDatatable } from '@/components/users/datatables';
import { PendingUserBanner } from '@/components/users/PendingUserBanner';
import { UsersCountSubheader } from '@/components/users/UsersCountSubheader';
import { useAccountUsers, useRoles } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { showBlockEmailsMessage } from '@/utils/helpers';
import { MenuItem } from 'primereact/menuitem';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

const PENDING_DELETION_STATUS = 'pending-deletion';

const getBreadcrumbs = (
  account: Account,
  t: TranslationFunctionType,
): MenuItem[] => {
  return [
    {
      label: account?.name,
      url: !account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('users'),
      url: RedirectPaths[RedirectPathsEnum.USERS](),
      template: AppBreadCrumbTemplate,
    },
  ];
};

export const UsersPage = () => {
  const { t } = useTranslation();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [searchParams] = useSearchParams();

  const forcedTableFilters =
    searchParams.get('status') === PENDING_DELETION_STATUS
      ? { active: PENDING_DELETION_STATUS }
      : undefined;

  const [allUsersCount, setAllUsersCount] = useState(0);
  const [filteredUsersCount, setFilteredUsersCount] = useState(0);

  const hasBlockEmailsBanner = showBlockEmailsMessage(currentAccount);

  const { users: usersData } = useAccountUsers({
    accountId: currentAccount?.id,
    take: 100,
    filters: [equal('account', currentAccount?.id as string)],
    enabled: !!currentAccount?.freeTrialEndsAt,
  });

  const pendingUsers = useMemo(
    () => usersData?.result?.filter((u) => u.accessState === 'pending') ?? [],
    [usersData],
  );

  const { roles: rolesData } = useRoles({
    take: 100,
    enabled: pendingUsers.length > 0,
  });

  const roles = rolesData?.result ?? [];

  return (
    <>
      {hasBlockEmailsBanner && <BlockEmailsBanner />}
      <AppBreadCrumb model={getBreadcrumbs(currentAccount as Account, t)} />
      <h1 className="mb-0">{t('users')}</h1>
      <UsersCountSubheader
        allUsersCount={allUsersCount}
        filteredUsersCount={filteredUsersCount}
      />
      {pendingUsers.map((user) => (
        <PendingUserBanner
          key={user.id}
          pendingUser={user}
          accountId={currentAccount?.id as string}
          roles={roles}
        />
      ))}
      <AddUsersBanner />
      <UserDatatable
        withActions
        withBranches
        withGroups={1}
        withAddNew
        withToolbar
        withImport
        defaultFilters={[equal('account', currentAccount?.id as string)]}
        defaultTableFilters={{ active: 'active' }}
        forcedTableFilters={forcedTableFilters}
        onUserCountChange={(allUsersCount, filteredUsersCount) => {
          setAllUsersCount(allUsersCount);
          setFilteredUsersCount(filteredUsersCount);
        }}
      />
    </>
  );
};
